import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

export interface ActiveSessionFromCache {
  sessionId: string;
  recordingStatus: RecordingStatus.RECORDING | RecordingStatus.PAUSED;
  createdAt: string | undefined;
}

/**
 * Walks the GetSessionById TanStack cache for any session whose
 * `recording_status` is RECORDING or PAUSED, returning the most recent by
 * `created_at`. Used as the last-resort sessionId source when the atom and
 * the route id are both unavailable.
 */
export function findActiveSessionFromCache(
  queryClient: QueryClient
): ActiveSessionFromCache | null {
  const entries = queryClient.getQueriesData<QuerySessionResponse>({
    queryKey: [GET_SESSION_BY_ID],
  });

  const active = entries.flatMap(([key, data]): ActiveSessionFromCache[] => {
    if (!data) return [];
    const status = data.recording_status;
    if (status !== RecordingStatus.RECORDING && status !== RecordingStatus.PAUSED) {
      return [];
    }
    const sessionId = extractSessionIdFromKey(key);
    if (!sessionId) return [];
    return [
      { sessionId, recordingStatus: status, createdAt: data.created_at ?? undefined },
    ];
  });

  active.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  return active[0] ?? null;
}

function extractSessionIdFromKey(key: QueryKey): string | null {
  const second: unknown = key[1];
  if (second === null || typeof second !== 'object') return null;
  const candidate: unknown = (second as { sessionId?: unknown }).sessionId;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
}

function toMillis(iso: string | undefined): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : 0;
}
